import { IAttendeeOrganizationDTO, IFilteredOrganization } from '../../api/attendees.dao';
import { AttendeesAction, AttendeesUpdateRole, IMeetingAttendees } from '../../api/meetings.dao';
import { ONLY_SPACES_REG_EXP, PHONE_REG_EXP } from '../../constants/regex.constants';
import { replaceStringWithHighlight } from '../../infrastructure/utils/common.utils';

export const getDisabled = (selected: IAttendeeOrganizationDTO[]) => {
    const disabled = {};

    selected.forEach(({ organizationId, attendees }) => {
        disabled[organizationId] = (attendees || []).reduce((prev, next) => {
            prev[next.attendeeId] = true;
            return prev;
        }, disabled[organizationId] || {});
    });

    return Object.entries(disabled).map(([organizationId, attendeesIds]) =>
        ({ organizationId: Number(organizationId), attendees: Object.keys(attendeesIds).map(id => ({ attendeeId: Number(id) })) }));
};
export const getFilteredAttendees = (
    items: IAttendeeOrganizationDTO[],
    filter: string,
    withHighlight: boolean = false,
    includePhones: boolean = false,
    showAllAttendees: boolean = false // if found only organization, should show all attendees, who belong to this organization
  ): IFilteredOrganization[] => {
    const filterCriteria = filter.replace(ONLY_SPACES_REG_EXP, '').trim();
    // searching by organization name
    return !includePhones ?
      items.reduce((filteredOrganizations, organization) => {
        if (organization.name.toUpperCase().indexOf(filterCriteria.toUpperCase()) !== -1) {
          filteredOrganizations.push({
            ...organization,
            ...(withHighlight ? { markedOrganization:  replaceStringWithHighlight(organization.name, filterCriteria) } : {})
          });
        } else {
          const filteredAttendees = organization.attendees.reduce((result, attendee) => {
            const { firstName, lastName } = attendee;
            const fullName = firstName.toUpperCase().concat(' ', lastName.toUpperCase());

            if (fullName.indexOf((filterCriteria.toUpperCase())) !== -1) {
              result.push({
                ...attendee,
                ...(withHighlight ? { markedName: replaceStringWithHighlight(`${firstName} ${lastName}`, filterCriteria) } : {})
              });
            }
            return result;
          }, []);

          if (filteredAttendees.length) {
            filteredOrganizations.push({
              ...organization,
              attendees: filteredAttendees
            });
          }
        }

        return filteredOrganizations;
      }, []) :
      items.reduce((filteredOrganizations, organization) => {
        let isOrganizationAdded = false;
        let organizationToAdd = {};

        if (organization.name.toUpperCase().indexOf(filterCriteria.toUpperCase()) !== -1) {
          organizationToAdd = {
            ...organization,
            ...(withHighlight ? { markedOrganization:  replaceStringWithHighlight(organization.name, filterCriteria) } : {}),
          };
          isOrganizationAdded = true;
        }

        const filteredAttendees = organization.attendees.reduce((result, attendee) => {
          let isAttendeeAdded = false;
          let attendeeToAdd = {};
          const { firstName, lastName, phoneNumber } = attendee;
          const fullName = firstName.toUpperCase().concat(' ', lastName.toUpperCase());

          if (phoneNumber && phoneNumber.replace(PHONE_REG_EXP, '').indexOf(filterCriteria) !== -1 ||
              phoneNumber && phoneNumber.indexOf(filterCriteria) !== -1) {

            const formattedSearchWord = filterCriteria.trim();
            const stringForRegExp = formattedSearchWord.split('').reduce((prev, next) =>
                isNaN(+next) ? '\\' + next + '*' + prev : prev + '\\D*' + next + '\\D*', ''
            );

            attendeeToAdd = {
              ...attendee,
              ...(withHighlight ? { markedPhone: replaceStringWithHighlight(phoneNumber, filterCriteria, stringForRegExp) } : {})
            };
            isAttendeeAdded = true;
          }

          if (fullName.indexOf(filterCriteria.toUpperCase()) !== -1) {
            if (isAttendeeAdded) {
              attendeeToAdd = {
                ...attendeeToAdd,
                ...(withHighlight ? { markedName: replaceStringWithHighlight(`${firstName} ${lastName}`, filterCriteria) } : {})
              };
            } else {
              attendeeToAdd = {
                ...attendee,
                ...(withHighlight ? { markedName: replaceStringWithHighlight(`${firstName} ${lastName}`, filterCriteria) } : {})
              };
            }
          }

          if (Object.keys(attendeeToAdd).length) {
            result.push(attendeeToAdd);
          }
          return result;
        }, []);

        if (isOrganizationAdded) {
          organizationToAdd = {
            ...organizationToAdd,
            attendees: showAllAttendees
              ? (filteredAttendees.length ? filteredAttendees : organization.attendees) // if found only org, return all attendees of this org
              : filteredAttendees
          };
        }
        if (!isOrganizationAdded && filteredAttendees.length) {
          organizationToAdd = {
            ...organization,
            attendees: filteredAttendees,
          };
        }

        if (Object.keys(organizationToAdd).length) {
          filteredOrganizations.push(organizationToAdd);
        }
        return filteredOrganizations;
    }, []);
};

export const getUpdatedAttendees = (
    organizationsAttendeesBeforeChange: IAttendeeOrganizationDTO[],
    organizationsAttendeesAfterChange: IAttendeeOrganizationDTO[],
    isAdded: boolean = true,
    roleKey: string
): IMeetingAttendees[] => {
    const updatedAttendeesInOrgs: IMeetingAttendees[] = [];

    const updatedOrganizations: IAttendeeOrganizationDTO[] = organizationsAttendeesBeforeChange.filter(orgBefore => {
        return organizationsAttendeesAfterChange.every(orgAfter => {
            if (orgAfter.organizationId === orgBefore.organizationId) { // if organization presents in meeting already
                const updatedAttendeesCurOrganization = orgBefore.attendees.filter(orgAttendeeBefore => {
                    return orgAfter.attendees.every(orgAttendeeAfter => {
                        return orgAttendeeAfter.attendeeId !== orgAttendeeBefore.attendeeId; // compare attendees in this organization
                    });
                });

                if (updatedAttendeesCurOrganization.length) { // if found difference in attendees
                    updatedAttendeesCurOrganization.map(val => {
                        updatedAttendeesInOrgs.push({
                            organizationId: orgBefore.organizationId,
                            attendeeId: val.attendeeId,
                            action: isAdded ? AttendeesAction.Add : AttendeesAction.Remove,
                            role: AttendeesUpdateRole[roleKey]
                        });
                    });
                }
            }
            return orgAfter.organizationId !== orgBefore.organizationId; // if it's new added organization, return new organization
        });
    });

    updatedOrganizations.map(organization => {
        if (organization.attendees.length === 0) {
            updatedAttendeesInOrgs.push({
                organizationId: organization.organizationId,
                attendeeId: null,
                action: isAdded ? AttendeesAction.Add : AttendeesAction.Remove,
                role: AttendeesUpdateRole[roleKey]
            });
        } else {
            organization.attendees.map(attendee => {
                updatedAttendeesInOrgs.push({
                    organizationId: organization.organizationId,
                    attendeeId: attendee.attendeeId,
                    action: isAdded ? AttendeesAction.Add : AttendeesAction.Remove,
                    role: AttendeesUpdateRole[roleKey]
                });
            });
        }
    });

    return updatedAttendeesInOrgs;
};

export const getAddedAttendees = (
    organizationsAttendeesBeforeChange: IAttendeeOrganizationDTO[],
    organizationsAttendeesAfterChange: IAttendeeOrganizationDTO[],
    roleKey: string
): IMeetingAttendees[] => {
    const updatedAttendeesInOrgs = getUpdatedAttendees(
        organizationsAttendeesBeforeChange,
        organizationsAttendeesAfterChange,
        true,
        roleKey
    );

    return updatedAttendeesInOrgs;
};

export const mergeConnectionOrganizations = (
    participants: IAttendeeOrganizationDTO[],
    webinarParticipants: IAttendeeOrganizationDTO[] = [],
    checkForDuplicates = false,
): IAttendeeOrganizationDTO[] => {
    if (!webinarParticipants?.length) {
        return participants;
    }
    const newParticipants = [...participants];
    const orgIdToIndexMap = {};
    newParticipants.forEach((org, i) => (orgIdToIndexMap[org.organizationId] = i));

    webinarParticipants.forEach(org => {
        if (orgIdToIndexMap.hasOwnProperty(org.organizationId)) {
            const index = orgIdToIndexMap[org.organizationId];
            const mappedOrg = newParticipants[index];
            if (checkForDuplicates) {
                const uniqIds = new Set(mappedOrg.attendees.map(attendee => attendee.attendeeId));
                newParticipants[index] = {
                    ...org,
                    ...mappedOrg,
                    attendees: [...org.attendees.filter(attendee => !uniqIds.has(attendee.attendeeId)), ...mappedOrg.attendees ]
                };
            } else {
                newParticipants[index] = {
                    ...org, ...mappedOrg, attendees: [...org.attendees, ...mappedOrg.attendees ]
                };
            }

        } else {
            newParticipants.push(org);
        }
    });

    return newParticipants;
};
